/**
 * Which What's new notes this browser has shown, and so which are unread.
 *
 * Stored per browser, as the ids of the notes that were visible the last time the
 * panel was opened. Ids rather than a date, so two notes published on the same day
 * are told apart.
 *
 * A browser with nothing stored counts the newest FIRST_VISIT_COUNT notes as unread.
 * Counting everything would light up a new device with the whole history; counting
 * nothing would hide the panel's own launch note from everybody, since on launch
 * day every browser is on its first visit (ADR 0042).
 */

const STORAGE_KEY = 'mikeai:whatsNewSeen';

/** How many of the newest notes a first visit counts as new. */
export const FIRST_VISIT_COUNT = 3;

/** The stored ids, or null when nothing is stored or the browser refuses storage. */
export function readSeen(): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

/** Remember these ids as seen. Only the current ids are kept, so the list never grows. */
export function writeSeen(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* storage unavailable (e.g. private browsing): the dot shows again next visit */
  }
}

/** The ids not seen yet, from notes ordered newest first. On a first visit (`seen` null), the newest few. */
export function unreadIds(notes: { id: string }[], seen: string[] | null): string[] {
  if (seen === null) return notes.slice(0, FIRST_VISIT_COUNT).map((n) => n.id);
  const known = new Set(seen);
  return notes.filter((n) => !known.has(n.id)).map((n) => n.id);
}
